import moment from "moment";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Row } from "react-bootstrap";
import { CheckCircle } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import DriveFileUploader from "../components/drive-file-uploader";
import FormGenerator from "../form-generator/FormGenerator";
import {
  getFormattedDate,
  highlightError,
  setAttribute,
} from "../form-generator/helpers/utility";
import { makeRESTApiRequests, sizeOf } from "../helpers/api";
import {
  ENDPOINTS,
  SERVICE_REQUEST_FILE_UPLOAD,
  STATUS,
  defaultProposedDateAppChoiceKey,
  defaultScheduledClosingDateDiffAppChoiceKey,
  labelToKeyMap,
  surveyForm,
} from "../helpers/constants";
import useAuth from "../hooks/useAuth";
import useAppChoices from "../hooks/useAppChoices";

const allowedRegex = /^[0-9,a,b,-]*$/i;
const selectHint = "Select one...";

const getPreviousFormData = () => {
  const previousData = localStorage.getItem("prev-request-form");
  if (previousData) {
    try {
      return JSON.parse(previousData);
    } catch (error) {
      return "";
    }
  }

  return "";
};

const saveFormData = (form) => {
  const toBeSavedForm = { ...form };
  ["titleCommitment", "previousSurvey", "previousDeed"].forEach(
    (fileField) => delete toBeSavedForm[fileField]
  );

  localStorage.setItem(
    "prev-request-form",
    JSON.stringify({ data: toBeSavedForm, date: moment().format("Do MMM") })
  );
};

const RequestService = ({ appChoices }) => {
  const [form, setForm] = useState(surveyForm(appChoices));
  const [isFormLoading, setFormLoading] = useState(true);
  const [isFormSubmitted, setFormSubmitted] = useState({
    success: false,
    mailSent: false,
  });
  const [submittingRequestForm, setSubmittingRequestForm] = useState(false);

  const [filesToBeUploaded, setFilesToBeUploaded] = useState({
    uploadTitleCommitment: [],
    uploadCopyOfPrevoiusSurvey: [],
    uploadCopyOfPrevoiusRecordDeeds: [],
  });

  const [availabilities, setAvailabilities] = useState();
  const [previousData] = useState(getPreviousFormData());
  const [preFillData, setPrefillData] = useState();
  const { user } = useAuth();
  const navigate = useNavigate();

  const proposedDateDiff =
    useAppChoices(defaultProposedDateAppChoiceKey)[0] || 12;
  const scheduledClosingDateDiff =
    useAppChoices(defaultScheduledClosingDateDiffAppChoiceKey)[0] || 14;

  const setupForm = async () => {
    getAvailabilities();
  };

  const getAvailabilities = async () => {
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.GET_HOLIDAYS,
    });

    if (error) {
      toast.error(error);
    }

    const availabilityReponse = response || [];

    setAvailabilities({
      holidays: availabilityReponse || [],
      proposedDateDiff: proposedDateDiff,
      scheduledClosingDateDiff: scheduledClosingDateDiff,
    });
  };

  useEffect(() => {
    if (!user?.email) setupForm();
  }, []);

  useEffect(() => {
    if (availabilities) {
      const { scheduledClosingDateDiff, proposedDateDiff } = availabilities;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const minProposedDueDate = findMinDate(today, proposedDateDiff);
      const minscheduledClosingDate = findMinDate(
        today,
        scheduledClosingDateDiff
      );

      const maxProposedDueDate = new Date(minProposedDueDate);
      maxProposedDueDate.setDate(maxProposedDueDate.getDate() + 60);

      setAttribute(
        form,
        "proposedDueDate",
        "min",
        getFormattedDate(minProposedDueDate)
      );

      setAttribute(
        form,
        "proposedDueDate",
        "max",
        getFormattedDate(maxProposedDueDate)
      );

      setAttribute(
        form,
        "titleClosingDate",
        "min",
        getFormattedDate(minscheduledClosingDate)
      );

      setForm({ ...form });
      setFormLoading(false);
    }
  }, [availabilities]);

  useEffect(() => {
    if (user?.email) navigate("/jobs/current");
  }, [user?.email]);

  const isWeekEnd = (date) => {
    return date.getDay() === 6 || date.getDay() === 0;
  };

  const findMinDate = (today, numOfDays) => {
    let returnDate = new Date(today);
    while (true) {
      if (numOfDays === 0) break;

      returnDate.setDate(returnDate.getDate() + 1);
      if (isWeekEnd(returnDate) || isHoliday(returnDate)) continue;

      numOfDays--;
    }

    return returnDate;
  };

  const isHoliday = (date) => {
    return availabilities.holidays.some(
      (h) =>
        h.year === date.getFullYear() &&
        h.month - 1 === date.getMonth() &&
        h.date === date.getDate()
    );
  };

  const onRequestFormSubmit = async (form) => {
    const { scheduledClosingDateDiff, proposedDateDiff } = availabilities;

    if (
      Object.keys(filesToBeUploaded).some((fileToBeUploaded) =>
        filesToBeUploaded[fileToBeUploaded]
          .map((f) => f?.status)
          .includes("pending")
      )
    ) {
      return toast.error("Please wait until files are getting uploaded");
    }

    const weekendBlockedDates = ["proposedDueDate", "titleClosingDate"];
    for (const blockedDate of weekendBlockedDates) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const date = new Date(form[blockedDate]);
      date.setHours(0, 0, 0, 0);

      const minDate =
        blockedDate === "proposedDueDate"
          ? findMinDate(today, proposedDateDiff)
          : findMinDate(today, scheduledClosingDateDiff);

      if (date.getTime() < minDate.getTime()) {
        highlightError(
          document.getElementById(blockedDate),
          `Please select a date further from ${moment(minDate).format(
            "Do MMMM"
          )}!`
        );
        return;
      }

      if (isWeekEnd(date)) {
        //weekend
        highlightError(
          document.getElementById(blockedDate),
          "Weekends are not allowed!"
        );
        return;
      }
    }

    if (form["propertyAddress"]?.length > 10) {
      highlightError(
        document.getElementById("propertyAddress"),
        "Please  enter less than 10 characters"
      );
      return;
    }

    if (
      form["propertyAddress"] &&
      !allowedRegex.test(form["propertyAddress"])
    ) {
      highlightError(
        document.getElementById("propertyAddress"),
        "Allowed characters are numbers and letters a & b."
      );
      return;
    }

    if (form["titleCompany"] === "Other") {
      form["titleCompany"] = form["titleOther"];
    }

    if (form["county"] === selectHint) {
      form["county"] = "";
    }

    form["propertyNumber"] = `'${form["propertyNumber"]}`;

    const formData = {};
    Object.keys(labelToKeyMap).forEach((label) => {
      formData[labelToKeyMap[label]] = form[labelToKeyMap[label]];
    });

    if (sizeOf(JSON.stringify(formData)) > 45) {
      toast(`Your attachments are too large, please replace with tinier ones`, {
        type: "error",
      });
      return;
    }

    formData["isThereAClosing"] = formData["titleCompany"] !== "NONE";

    toast(
      `Your Quote is being Submitted.
    This might take some time if you have attachments uploaded...`,
      {
        type: "info",
        autoClose: 5000,
      }
    );

    setSubmittingRequestForm(true);

    const { error, response } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.CREATE_JOBS,
      requestBody: {
        ...formData,
        status: STATUS[0],
        fileIds: Object.keys(filesToBeUploaded).flatMap((fileToBeUploaded) =>
          filesToBeUploaded[fileToBeUploaded]
            .map((i) => i?.fileId)
            .filter(Boolean)
        ),
      },
    });
    if (error) {
      toast.error(error, { autoClose: 5000 });
      setSubmittingRequestForm(false);
      return;
    }

    setFormSubmitted({ success: true, mailSent: response["mailSent"] });
    saveFormData(form);
    setSubmittingRequestForm(false);
  };

  const onClientDifferentChange = (e) => {
    const checked = document.getElementById("isClientDifferent").checked;

    const clientName = document.getElementById("clientName");
    const clientEmail = document.getElementById("clientEmail");
    const contractWarningText = document.getElementById("contractWarningText");

    setAttribute(form, "clientName", "required", checked);
    setAttribute(form, "clientEmail", "required", checked);

    if (checked) {
      clientName.removeAttribute("disabled");
      clientEmail.removeAttribute("disabled");
      contractWarningText.classList.remove("d-none");
    } else {
      clientName.value = "";
      clientEmail.value = "";
      clientName.setAttribute("disabled", true);
      clientEmail.setAttribute("disabled", true);
      contractWarningText.classList.add("d-none");
    }
    setForm({ ...form });
  };

  const onUploadedFilesChange = (files, key) => {
    const newFiles = [...files];

    setFilesToBeUploaded((prevUploadedFiles) => ({
      ...prevUploadedFiles,
      [key]: newFiles,
    }));
  };

  window["onClientDifferentChange"] = onClientDifferentChange;
  window["onRequestFormSubmit"] = onRequestFormSubmit;

  const onRequestFormClick = () => {
    document.getElementById("request-form-submit").click();
  };

  const SubmitSuccessLayout = () => {
    return (
      <Row className="justify-content-center text-center flex-grow-1">
        <Col xs={12} lg={12} className="px-3 align-self-center">
          <CheckCircle className="text-success" size={60} />
          <h2 className="my-4">
            <b>Thank You!</b>
          </h2>

          <div className="w-50 mx-auto">
            <h5>
              We have received your quote request. We will send you a
              confirmation email shortly. Our team will get back to you as soon
              as possible!
            </h5>
          </div>

          {!isFormSubmitted["mailSent"] && (
            <Alert variant="warning" className="mt-3" style={{ fontSize: 14 }}>
              We were unable to send you an email on the address you provided -
              but we have recorded your quote and you can rest assured that our
              team is on it.
            </Alert>
          )}
          <a href="/">
            <Button className="text-white mt-4" variant="primary" size="lg">
              Back To Website
            </Button>
          </a>
        </Col>
      </Row>
    );
  };

  const onPreFillPreviousDataClick = () => {
    setFormLoading(true);
    setPrefillData(previousData.data);
    setTimeout(() => setFormLoading(false), 200);
  };

  return (
    <Container fluid className="h-100 p-1" style={{ overflowX: "hidden" }}>
      <div className="h-100">
        <div>
          {isFormSubmitted["success"] ? (
            <SubmitSuccessLayout />
          ) : (
            <Row className="flex-grow-1 ">
              <Col xs={12} md={12}>
                <div className="d-flex">
                  {previousData && !preFillData && (
                    <div className="mb-2 ml-auto">
                      <Button
                        style={{ fontSize: 11 }}
                        className="px-2 py-1"
                        onClick={onPreFillPreviousDataClick}
                        size="sm"
                      >
                        Fill data from {previousData.date}
                      </Button>
                    </div>
                  )}
                </div>

                <Card>
                  <Card.Body className="p-1">
                    {isFormLoading && isFormLoading ? (
                      <Loader />
                    ) : (
                      <>
                        <FormGenerator
                          formJson={form}
                          formValues={{
                            "request-form-submit": preFillData,
                          }}
                        />
                        <Row className="px-4 my-2 align-items-end">
                          {SERVICE_REQUEST_FILE_UPLOAD.map(({ label, key }) => {
                            return (
                              <Col xs={12} md={4}>
                                <DriveFileUploader
                                  id={label}
                                  multiple={false}
                                  deletable={false}
                                  key={label}
                                  size="sm"
                                  title={label}
                                  filesToBeUploaded={filesToBeUploaded[key]}
                                  onUploadedFilesChange={(files) =>
                                    onUploadedFilesChange(files, key)
                                  }
                                />
                              </Col>
                            );
                          })}
                        </Row>

                        <h6 class="text-muted ml-3 tiny-text my-1">
                          Email any additional documents or things needed to
                          Adam@aTmSurveying.com
                        </h6>

                        <Row className="mx-2">
                          <Col className="bg-light text-md-right mt-md-0 py-1">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={onRequestFormClick}
                              disabled={submittingRequestForm}
                            >
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </Container>
  );
};

export default RequestService;
